/**
 * Pure delta-merge functions for the snapshot ``FeatureCollection``.
 *
 * Stream A PR 2 / commit M3 — extracted from the React + MapLibre
 * lifecycle so the merge logic is unit-testable independent of
 * either. The :class:`SnapshotLayer` calls
 * :func:`mergeStatusIntoFeatureCollection` after receiving each
 * ``status`` SSE event, then writes the result to the GeoJSON source
 * via :func:`maplibregl.GeoJSONSource#setData`.
 *
 * # Semantics — match-by-producer_id; update-in-place; append-new
 *
 * For ``status`` payloads:
 *   - Match the incoming :type:`SseStatusPayload`'s ``producer_id``
 *     against the existing feature's ``properties.producer_id``.
 *   - If a match exists, replace the feature in place — preserves
 *     array order so the marker layer paints incrementally rather
 *     than reflowing.
 *   - If no match, append the new feature. Newly-onboarded
 *     Producers that appear via SSE before the next snapshot
 *     refresh still show on the map.
 *
 * For ``event`` payloads:
 *   - M3 does NOT merge events into the snapshot source — events
 *     are not Producers; they're transient detections. M5 (trail)
 *     and M6 (Entity Details panel) consume the event stream
 *     separately. M3 just routes the message; the snapshot source
 *     is unchanged.
 *
 * # Why a fresh ``FeatureCollection`` per merge
 *
 * MapLibre's :class:`GeoJSONSource` requires a complete data object
 * on every :func:`setData` call — it doesn't accept incremental
 * patches. The pure function returns a new collection so callers
 * can reason about immutability + so React's referential-equality
 * checks fire on every snapshot update.
 *
 * Cross-references:
 *   - REQ-UI-006 (live update marker patch ≤ 100 ms — pure-function
 *     merge keeps the per-event cost trivial).
 *   - REQ-UI-009 (snapshot before SSE resume — the snapshot is the
 *     authoritative base; merges layer on top).
 *   - ADR-016 §1 + §3 (snapshot+SSE pattern).
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Live update cadence and behavior".
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Frame 4 — Reconnection succeeds; snapshot refresh".
 */

import type { SseStatusPayload } from "@/map/sse-types";

/**
 * Feature properties on each :type:`GeoJSON.Feature` in the
 * snapshot's ``FeatureCollection``. Mirrors
 * :file:`snapshot-layer-impl.ts` §snapshotItemToFeature — keep
 * synchronized when either side changes.
 *
 * Exported so callers (the merge function + its tests) can rely on
 * the shape; the snapshot-layer's MapLibre paint expression keys on
 * the same fields.
 */
export interface MarkerFeatureProperties {
  producer_id: string;
  health_state: number;
  /**
   * ``true`` when the producer's data arrived via the CDS (issue #278) —
   * derived from a non-empty ``source_network``. The snapshot layer's
   * paint keys on this for the REQ-UI-014 provenance marker variant.
   */
  crossed_cds: boolean;
  /**
   * Operator-facing label rendered below the glyph (PR #297, REQ-UI of the
   * map glyph label). The BFF resolves this from the control-plane registry
   * on the ``/snapshot`` join; the high-frequency ``status`` SSE stream
   * carries raw status only (no registry join), so a live update has no
   * ``display_name`` of its own. The merge therefore PRESERVES the existing
   * feature's ``display_name`` across an in-place update — otherwise the
   * first SSE tick would replace the labelled snapshot feature with an
   * unlabelled one and the glyph label would vanish within seconds. Empty
   * string when no snapshot label has been seen yet (a producer that first
   * appears via SSE before the next snapshot refresh resolves its name).
   */
  display_name: string;
}

/**
 * A GeoJSON ``Feature<Point>`` carrying the marker properties the
 * snapshot layer's paint expression reads.
 *
 * The ``properties`` are non-null and typed — the merge function
 * builds features matching this shape; the snapshot-layer's
 * conversion helper produces the same shape.
 */
export type MarkerFeature = GeoJSON.Feature<GeoJSON.Point, MarkerFeatureProperties>;

/**
 * The snapshot source's ``FeatureCollection`` shape. ``features`` is
 * always an array of :type:`MarkerFeature` — empty when no Producer
 * has reported within the viewport.
 */
export type MarkerFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Point,
  MarkerFeatureProperties
>;

/**
 * Build a :type:`MarkerFeature` from an SSE status payload.
 *
 * Mirrors :file:`snapshot-layer-impl.ts` §snapshotItemToFeature so
 * the merge product is paint-spec-compatible with the layer that
 * consumes it.
 *
 * ``displayName`` is carried over from the feature this payload replaces
 * (the ``status`` SSE stream has no registry-resolved name of its own — see
 * :type:`MarkerFeatureProperties`'s ``display_name`` doc). Defaults to the
 * empty string for a producer first seen via SSE, which places no label
 * until the next snapshot refresh resolves the name.
 *
 * Exported for unit testability.
 *
 * Cross-references:
 *   - REQ-UI-006.
 */
export function statusPayloadToFeature(payload: SseStatusPayload, displayName = ""): MarkerFeature {
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [payload.longitude_deg, payload.latitude_deg],
    },
    properties: {
      producer_id: payload.producer_id,
      health_state: payload.health_state,
      crossed_cds: payload.source_network != null && payload.source_network !== "",
      display_name: displayName,
    },
  };
}

/**
 * Merge an :type:`SseStatusPayload` into an existing snapshot
 * ``FeatureCollection``. Match-by-``producer_id``; update-in-place
 * preserves array order so MapLibre's WebGL paint operates
 * incrementally.
 *
 * Returns a fresh :type:`MarkerFeatureCollection` — the input is
 * not mutated. New ``features`` array; existing :type:`MarkerFeature`
 * references are preserved when not updated, so React's
 * referential-equality checks (e.g., in :func:`useMemo`) fire only
 * for the changed feature.
 *
 * Edge cases:
 *   - Empty input collection + new payload → single-feature output.
 *   - Match found → in-place replacement; index preserved.
 *   - No match → appended to the end of ``features``.
 *
 * Cross-references:
 *   - REQ-UI-006 (live update marker patch within 100 ms — the
 *     merge's O(N) scan over ~150 Producers at MVP completes in
 *     well under 1 ms; the budget is dominated by MapLibre's paint).
 *   - ADR-016 §1 (snapshot+SSE — SSE patches the snapshot in
 *     place).
 */
export function mergeStatusIntoFeatureCollection(
  collection: MarkerFeatureCollection,
  payload: SseStatusPayload,
): MarkerFeatureCollection {
  let matched = false;
  const features = collection.features.map((existing) => {
    if (existing.properties.producer_id === payload.producer_id) {
      matched = true;
      // Preserve the registry-resolved label across the in-place update —
      // the SSE status payload carries no display_name of its own, so
      // rebuilding the feature without it would blank the glyph label on the
      // first live tick (the original PR #297 defect).
      return statusPayloadToFeature(payload, existing.properties.display_name);
    }
    return existing;
  });
  if (!matched) {
    // New producer seen via SSE before the next snapshot — no label yet.
    features.push(statusPayloadToFeature(payload));
  }
  return {
    type: "FeatureCollection",
    features,
  };
}

/**
 * Type-narrowing helper — convert a generic
 * :type:`GeoJSON.FeatureCollection<GeoJSON.Point>` (the shape the
 * snapshot-layer registers) into the merge's
 * :type:`MarkerFeatureCollection`.
 *
 * The snapshot-layer's conversion writes ``properties.producer_id``
 * + ``properties.health_state`` per
 * :file:`snapshot-layer-impl.ts` §snapshotItemToFeature, but its
 * type annotation is the more permissive :type:`GeoJSON.GeoJsonProperties`
 * (a ``Record<string, unknown> | null``). This narrowing keeps the
 * merge function's input typed without forcing every caller to
 * cast.
 *
 * Exported for symmetry with the producer side and for direct test
 * coverage of the cast.
 */
export function narrowToMarkerCollection(
  collection: GeoJSON.FeatureCollection<GeoJSON.Point>,
): MarkerFeatureCollection {
  return collection as MarkerFeatureCollection;
}
